// GixCore audience analytics client.
//
// Best-effort, fire-and-forget visitor/session telemetry. Every call swallows
// its own errors: analytics must NEVER break navigation, auth, or the buy link.
// The backend may answer 202 { captured: false } (DNT/GPC, analytics disabled,
// consent denied, or migrations missing) — that is a normal, silent outcome.
//
// IDs:
//   visitorId  -> localStorage   (persists across sessions/tabs on this device)
//   sessionId  -> sessionStorage (one per tab/session)
//
// Both IDs are sent as headers (x-gixcore-session-id / x-gixcore-visitor-id) and
// duplicated in the request body, so unload-time sendBeacon calls (which cannot
// set headers) still carry identity.

import axios from 'axios'
import { apiDomain } from '../config'

const VISITOR_KEY = 'gixcore_visitor_id'
const SESSION_KEY = 'gixcore_session_id'
const ATTRIB_KEY = 'gixcore_first_touch'
const HEARTBEAT_MS = 30000
const VISITOR_TTL_S = 730 * 24 * 60 * 60 // ~2 years
const ATTRIB_TTL_S = 90 * 24 * 60 * 60   // 90 days
const SESSION_TTL_MS = 30 * 60 * 1000    // 30-min rolling idle (standard session)

let booted = false
let heartbeatTimer = null
let lastTrackedPath = ''
let adminSuppressed = false
let publicSessionStarted = false
let memSession = '' // in-memory session record fallback when storage is blocked
let consent = { analytics: true, marketing: false }

function uuid () {
  try {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return window.crypto.randomUUID()
    }
  } catch (e) { /* fall through */ }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

function readStore (store, key) {
  try { return store.getItem(key) || '' } catch (e) { return '' }
}

function writeStore (store, key, value) {
  try { store.setItem(key, value) } catch (e) { /* private mode / disabled */ }
}

function removeStore (store, key) {
  try { store.removeItem(key) } catch (e) { /* private mode / disabled */ }
}

// Every GixCore property is *.gixcore.com, so treat those (and dev hosts) as "us":
// an own-domain referrer is a self-referral, never a traffic source.
function isOwnHost (host) {
  if (!host) return false
  return host === 'localhost' || host === '127.0.0.1' || /(^|\.)gixcore\.com$/i.test(host)
}

// Cookies shared across *.gixcore.com so visitor identity + first-touch attribution
// survive the gixcore.com (landing) -> beta.gixcore.com (builder) hop, where
// localStorage is per-origin and would otherwise split them.
function cookieDomain () {
  try {
    var h = window.location.hostname
    if (h === 'localhost' || h === '127.0.0.1') return ''
    return /(^|\.)gixcore\.com$/i.test(h) ? '.gixcore.com' : ''
  } catch (e) { return '' }
}

function readCookie (name) {
  try {
    var m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'))
    return m ? decodeURIComponent(m[1]) : ''
  } catch (e) { return '' }
}

function writeCookie (name, value, maxAgeSeconds) {
  try {
    var maxAge = Math.ceil(maxAgeSeconds)
    var domain = cookieDomain()
    var secure = window.location.protocol === 'https:' ? '; Secure' : ''
    var dom = domain ? '; Domain=' + domain : ''
    document.cookie = name + '=' + encodeURIComponent(value) + '; Max-Age=' + maxAge + '; Path=/' + dom + '; SameSite=Lax' + secure
  } catch (e) { /* disabled */ }
}

function getVisitorId () {
  // Cookie wins so the visitor is unified across subdomains; localStorage mirrors it.
  let id = readCookie(VISITOR_KEY) || readStore(window.localStorage, VISITOR_KEY)
  if (!id) id = uuid()
  writeCookie(VISITOR_KEY, id, VISITOR_TTL_S)
  writeStore(window.localStorage, VISITOR_KEY, id)
  return id
}

// Session id shared across *.gixcore.com so landing (gixcore.com) and builder
// (beta.gixcore.com) are ONE session. Stored as "id.lastActivityMs": the timestamp
// makes the 30-min idle expiry unambiguous regardless of cookie availability.
function getSessionId () {
  const now = Date.now()
  const raw = readCookie(SESSION_KEY) || readStore(window.sessionStorage, SESSION_KEY) || memSession
  let id = ''
  if (raw) {
    const dot = raw.lastIndexOf('.')
    const sid = dot > 0 ? raw.slice(0, dot) : raw
    const ts = dot > 0 ? parseInt(raw.slice(dot + 1), 10) : 0
    if (sid && ts && (now - ts) <= SESSION_TTL_MS) id = sid
  }
  if (!id) id = uuid()
  const rec = id + '.' + now
  writeCookie(SESSION_KEY, rec, SESSION_TTL_MS / 1000) // rolling: refresh on each call
  writeStore(window.sessionStorage, SESSION_KEY, rec)
  memSession = rec
  return id
}

function resetSessionId () {
  writeCookie(SESSION_KEY, '', 0)
  removeStore(window.sessionStorage, SESSION_KEY)
  memSession = ''
}

// Identity headers for analytics calls AND login/google-auth calls.
function headers () {
  return {
    'x-gixcore-session-id': getSessionId(),
    'x-gixcore-visitor-id': getVisitorId()
  }
}

function identity () {
  return { sessionId: getSessionId(), visitorId: getVisitorId() }
}

function queryParam (params, names) {
  for (const n of names) {
    const v = params.get(n)
    if (v) return v
  }
  return undefined
}

function collectCampaign () {
  let params
  try { params = new URLSearchParams(window.location.search) } catch (e) { return {} }
  const out = {}
  const utm = {
    source: queryParam(params, ['utm_source']),
    medium: queryParam(params, ['utm_medium']),
    campaign: queryParam(params, ['utm_campaign']),
    term: queryParam(params, ['utm_term']),
    content: queryParam(params, ['utm_content'])
  }
  if (Object.keys(utm).some((k) => utm[k])) out.utm = utm
  const gclid = queryParam(params, ['gclid'])
  const fbclid = queryParam(params, ['fbclid'])
  const msclkid = queryParam(params, ['msclkid'])
  if (gclid) out.gclid = gclid
  if (fbclid) out.fbclid = fbclid
  if (msclkid) out.msclkid = msclkid
  const mc = queryParam(params, ['marketingCampaignId', 'marketing_campaign_id', 'mc', 'campaignId', 'campaign_id'])
  if (mc) out.marketingCampaignId = mc
  return out
}

function hasCampaign (c) {
  return Boolean(c.utm && Object.keys(c.utm).some((k) => c.utm[k])) ||
    Boolean(c.gclid || c.fbclid || c.msclkid || c.marketingCampaignId)
}

// document.referrer, but ONLY when it points outside our own properties. An
// own-domain referrer (e.g. arriving from the gixcore.com landing) is a
// self-referral and must not be recorded as a traffic source.
function externalReferrer () {
  const ref = safe(() => document.referrer) || ''
  if (!ref) return ''
  const host = safe(() => new URL(ref).hostname) || ''
  return host && !isOwnHost(host) ? ref : ''
}

// First-touch attribution: the entry referrer/utm captured ONCE and persisted
// (cookie + localStorage) so later self-referral page views never overwrite the
// real source. Shared across *.gixcore.com via the cookie.
function firstTouch () {
  const stored = readCookie(ATTRIB_KEY) || readStore(window.localStorage, ATTRIB_KEY)
  if (stored) {
    const parsed = safe(() => JSON.parse(stored))
    if (parsed && typeof parsed === 'object') return parsed
  }
  const ft = Object.assign({
    referrer: externalReferrer(),
    landingUrl: safe(() => window.location.href) || '',
    landingAt: new Date().toISOString(),
    origin: safe(() => window.location.origin) || ''
  }, collectCampaign())
  const json = JSON.stringify(ft)
  writeCookie(ATTRIB_KEY, json, ATTRIB_TTL_S)
  writeStore(window.localStorage, ATTRIB_KEY, json)
  return ft
}

function safe (fn) {
  try { return fn() } catch (e) { return undefined }
}

// POST with identity headers + body. Never throws; returns the response data or
// null. A 202 captured:false response resolves normally (best-effort capture).
async function post (endpoint, payload) {
  try {
    const { data } = await axios.post(
      `${apiDomain}audience/${endpoint}`,
      Object.assign({ occurredAt: new Date().toISOString() }, identity(), payload || {}),
      { headers: headers() }
    )
    return data
  } catch (e) {
    return null
  }
}

// Admin-only routes are operational traffic, not audience traffic. Erase the
// current public session at the source, then rotate its id so a later return to
// the builder starts a clean public session.
async function excludeCurrentSession (path) {
  const ids = identity()
  resetSessionId()
  try {
    const { data } = await axios.post(
      `${apiDomain}audience/exclude`,
      Object.assign({
        occurredAt: new Date().toISOString(),
        path: path || currentPath(),
        reason: 'admin_route'
      }, ids),
      {
        headers: {
          'x-gixcore-session-id': ids.sessionId,
          'x-gixcore-visitor-id': ids.visitorId
        }
      }
    )
    return data
  } catch (e) {
    return null
  }
}

// Unload-safe send. sendBeacon can't set headers, so identity rides in the body.
function beacon (endpoint, payload) {
  const body = Object.assign({ occurredAt: new Date().toISOString() }, identity(), payload || {})
  try {
    const url = `${apiDomain}audience/${endpoint}`
    const blob = new Blob([JSON.stringify(body)], { type: 'application/json' })
    if (navigator.sendBeacon && navigator.sendBeacon(url, blob)) return
  } catch (e) { /* fall through to axios */ }
  post(endpoint, payload)
}

function startPayload (path) {
  const ft = firstTouch()
  const live = collectCampaign()
  // Live campaign wins for the current click; otherwise fall back to first-touch.
  const campaign = hasCampaign(live) ? live : {
    utm: ft.utm,
    gclid: ft.gclid,
    fbclid: ft.fbclid,
    msclkid: ft.msclkid,
    marketingCampaignId: ft.marketingCampaignId
  }
  return Object.assign({
    path: path,
    url: safe(() => window.location.href),
    title: safe(() => document.title),
    // External referrer for THIS page, else the persisted entry referrer. Never self.
    referrer: externalReferrer() || ft.referrer || '',
    firstTouch: ft,
    siteOrigin: safe(() => window.location.origin),
    siteHost: safe(() => window.location.hostname),
    language: safe(() => navigator.language),
    timezone: safe(() => Intl.DateTimeFormat().resolvedOptions().timeZone),
    screen: {
      width: safe(() => window.screen.width),
      height: safe(() => window.screen.height)
    },
    viewport: {
      width: safe(() => window.innerWidth),
      height: safe(() => window.innerHeight)
    },
    consent: { analytics: consent.analytics !== false, marketing: consent.marketing === true }
  }, campaign)
}

function currentPath () {
  return safe(() => window.location.pathname + window.location.search) || '/'
}

// ---- public API ----

function setConsent (next) {
  consent = Object.assign({}, consent, next || {})
}

function trackPageView (path) {
  if (adminSuppressed) return
  const p = path || currentPath()
  if (p === lastTrackedPath) return
  lastTrackedPath = p
  post('event', {
    eventType: 'page_view',
    path: p,
    url: safe(() => window.location.href),
    title: safe(() => document.title)
  })
}

function trackSearch (query, data) {
  if (adminSuppressed) return
  post('event', {
    eventType: 'search',
    path: currentPath(),
    data: Object.assign({ query: query == null ? '' : String(query) }, data || {})
  })
}

function trackAmazonClick (info) {
  if (adminSuppressed) return
  const i = info || {}
  post('event', {
    eventType: 'amazon_click',
    path: currentPath(),
    targetType: 'part',
    targetId: i.targetId != null ? i.targetId : (i.id != null ? i.id : null),
    data: {
      asin: i.asin || null,
      url: i.url || null,
      partType: i.partType || i.type || null,
      model: i.model || null,
      price: i.price != null ? i.price : null,
      source: i.source || null
    }
  })
}

// A build reached its completed state (every slot filled) on a given surface.
// Fired once per surface per build signature so a re-render is not a new event.
function trackBuildCompleted (info) {
  if (adminSuppressed) return
  const i = info || {}
  post('event', {
    eventType: 'build_completed',
    path: currentPath(),
    targetType: 'build',
    targetId: i.buildId != null ? i.buildId : null,
    data: {
      source: i.source || null,
      partsCount: i.partsCount != null ? i.partsCount : null,
      cartablePartsCount: i.cartablePartsCount != null ? i.cartablePartsCount : null,
      missingAsinCount: i.missingAsinCount != null ? i.missingAsinCount : null,
      totalBand: i.totalBand || null,
      total: i.total != null ? i.total : null,
      compatibility: i.compatibility || null,
      conflicts: i.conflicts != null ? i.conflicts : null,
      warnings: i.warnings != null ? i.warnings : null,
      performanceTier: i.performanceTier || null,
      estimatedPower: i.estimatedPower != null ? i.estimatedPower : null,
      psuWattage: i.psuWattage != null ? i.psuWattage : null
    }
  })
}

// "Buy this build on Amazon" was clicked. Carries the whole-cart shape so the
// funnel can be read without joining back to per-part amazon_click events.
function trackBuildCartClick (info) {
  if (adminSuppressed) return
  const i = info || {}
  post('event', {
    eventType: 'amazon_cart_click',
    path: currentPath(),
    targetType: 'build',
    targetId: i.buildId != null ? i.buildId : null,
    data: {
      source: i.source || null,
      mode: i.mode || null,
      url: i.url || null,
      asins: Array.isArray(i.asins) ? i.asins : null,
      partsCount: i.partsCount != null ? i.partsCount : null,
      cartablePartsCount: i.cartablePartsCount != null ? i.cartablePartsCount : null,
      missingAsinCount: i.missingAsinCount != null ? i.missingAsinCount : null,
      unitCount: i.unitCount != null ? i.unitCount : null,
      totalBand: i.totalBand || null,
      total: i.total != null ? i.total : null,
      conflicts: i.conflicts != null ? i.conflicts : null,
      warnings: i.warnings != null ? i.warnings : null,
      performanceTier: i.performanceTier || null
    }
  })
}

// Generic UI button/CTA click. `name` identifies the button (e.g. "select-part",
// "compare", "reset-build"); `data` carries any extra context.
function trackClick (name, data) {
  if (adminSuppressed) return
  post('event', {
    eventType: 'button_click',
    path: currentPath(),
    data: Object.assign({ button: name == null ? '' : String(name) }, data || {})
  })
}

// Product interaction. `action` is one of select | add | remove | view; `product`
// is a part-like object. Records which product (id/asin/type) was acted on.
function trackProduct (action, product, extra) {
  if (adminSuppressed) return
  const p = product || {}
  post('event', {
    eventType: 'product_' + (action || 'click'),
    path: currentPath(),
    targetType: 'part',
    targetId: p.id != null ? p.id : (p.targetId != null ? p.targetId : null),
    data: Object.assign({
      action: action || 'click',
      asin: p.asin || null,
      partType: p.type || p.partType || null,
      model: p.model || (p.brand && p.model ? `${p.brand} ${p.model}` : null) || null,
      price: p.amzn_price != null ? p.amzn_price : (p.price != null ? p.price : null)
    }, extra || {})
  })
}

function heartbeat (useBeacon) {
  if (adminSuppressed || !publicSessionStarted) return
  const payload = { path: currentPath() }
  if (useBeacon) beacon('heartbeat', payload)
  else post('heartbeat', payload)
}

function end () {
  if (adminSuppressed || !publicSessionStarted) return
  beacon('end', { path: currentPath() })
}

function startHeartbeat () {
  stopHeartbeat()
  heartbeatTimer = window.setInterval(() => {
    if (safe(() => document.visibilityState) === 'hidden') return
    heartbeat(false)
  }, HEARTBEAT_MS)
}

function stopHeartbeat () {
  if (heartbeatTimer) {
    window.clearInterval(heartbeatTimer)
    heartbeatTimer = null
  }
}

function isAdminRoute (route) {
  return Boolean(route && Array.isArray(route.matched) && route.matched.some((record) => {
    return record && record.meta && record.meta.requiresAdmin === true
  }))
}

function startPublicSession (path, rotateSession) {
  if (rotateSession) resetSessionId()
  adminSuppressed = false
  publicSessionStarted = true
  lastTrackedPath = path
  post('session', startPayload(path))
  startHeartbeat()
}

function suppressAdminSession (path) {
  if (!adminSuppressed || publicSessionStarted) excludeCurrentSession(path)
  adminSuppressed = true
  publicSessionStarted = false
  lastTrackedPath = path
  stopHeartbeat()
}

// Boot once: register the session, start the heartbeat, and bind lifecycle +
// route hooks. Safe to call multiple times.
function init (router) {
  if (booted) return
  booted = true

  const path = currentPath()
  const initialRoute = router && router.currentRoute
  if (isAdminRoute(initialRoute)) suppressAdminSession(path)
  else startPublicSession(path, false)

  // Route changes (SPA) -> page_view events.
  if (router && typeof router.afterEach === 'function') {
    router.afterEach((to) => {
      const p = (to && to.fullPath) || currentPath()
      if (isAdminRoute(to)) {
        suppressAdminSession(p)
      } else if (adminSuppressed || !publicSessionStarted) {
        startPublicSession(p, true)
      } else {
        trackPageView(p)
      }
    })
  }

  try {
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden' && !adminSuppressed) heartbeat(true)
    })
    window.addEventListener('pagehide', () => end())
  } catch (e) { /* SSR / no DOM */ }
}

export default {
  init,
  setConsent,
  headers,
  identity,
  getSessionId,
  getVisitorId,
  trackPageView,
  trackSearch,
  trackAmazonClick,
  trackBuildCompleted,
  trackBuildCartClick,
  trackClick,
  trackProduct,
  heartbeat,
  end
}
